/**
 * Create-deal wizard — static config (dashboard slot E).
 *
 * Ported from the founder-approved mockup, refined by the Banker across four
 * council passes: instrument-aware term with a capped custom entry, cash-comfort
 * health question, a plain before-pay-tax-interest EBITDA proxy, and honest
 * ceilings. Kept inform-not-advise: no lender is named or ranked here.
 */

import type { FinancingPurpose, TimelineUrgency } from '../../../api/types';

/** Instrument key space — includes the synthetic asset-backed acquisition split. */
export type Instrument = FinancingPurpose | 'acquisition_asset';

export interface PurposeOption {
  k: FinancingPurpose;
  label: string;
}

export const PURPOSES: PurposeOption[] = [
  { k: 'acquisition', label: 'Acquisition' },
  { k: 'equipment', label: 'Equipment' },
  { k: 'real_estate', label: 'Real estate' },
  { k: 'working_capital', label: 'Working capital' },
  { k: 'refinancing', label: 'Refinancing' },
  { k: 'expansion', label: 'Growth / expansion' },
  { k: 'other', label: 'Other' },
];

/** Purposes offered in the "closest to?" select when the user picks "Other". */
export const OTHER_MAP_KEYS: FinancingPurpose[] = [
  'acquisition',
  'equipment',
  'real_estate',
  'working_capital',
  'refinancing',
  'expansion',
];

export interface TermCfg {
  chips: string[];
  anchor: string;
  min: number;
  max: number;
}

/** Instrument-aware term menus. Working capital has none (revolving line). */
export const TERMCFG: Record<string, TermCfg> = {
  acquisition: { chips: ['5', '7', '10'], anchor: '7', min: 3, max: 10 },
  acquisition_asset: { chips: ['7', '10', '15'], anchor: '10', min: 3, max: 20 },
  equipment: { chips: ['3', '5', '7', '10'], anchor: '5', min: 2, max: 10 },
  real_estate: { chips: ['10', '15', '20', '25'], anchor: '20', min: 5, max: 25 },
  refinancing: { chips: ['5', '7', '10', '15', '20'], anchor: '7', min: 3, max: 20 },
  expansion: { chips: ['5', '7', '10'], anchor: '7', min: 3, max: 10 },
  other: { chips: ['5', '7', '10'], anchor: '7', min: 2, max: 15 },
};

export interface HealthOption {
  k: 'healthy' | 'steady' | 'tight' | 'unsure';
  label: string;
}

export const HEALTH: HealthOption[] = [
  { k: 'healthy', label: 'Healthy — it generates cash to spare' },
  { k: 'steady', label: 'Steady — around break-even' },
  { k: 'tight', label: 'Tight — running at a loss right now' },
  { k: 'unsure', label: 'Not sure' },
];

export interface UrgencyOption {
  k: TimelineUrgency;
  label: string;
}

export const URGENCY: UrgencyOption[] = [
  { k: 'urgent', label: 'As soon as possible' },
  { k: 'normal', label: 'Within a few months' },
  { k: 'flexible', label: 'No fixed timeline' },
  { k: 'exploratory', label: 'Exploring for now' },
];

export interface Band {
  l: string;
  lo: number;
  hi: number;
}

/** Own-contribution bands (the borrower's self-reported equity share). Stored raw as
 * `own_contribution_band`. The old `ltv` midpoints were dropped — nothing read them
 * (the LTV readout was retired; Banker-confirmed 2026-07 to keep the band as-is). */
export const CONTRIB: { l: string }[] = [
  { l: '<10%' },
  { l: '10–20%' },
  { l: '20–30%' },
  { l: '30–40%' },
  { l: '>40%' },
];

/** EBITDA-proxy bands (EUR) with edges for the leverage range. */
export const EBITDA: Band[] = [
  { l: '<€50k', lo: 25000, hi: 50000 },
  { l: '€50–100k', lo: 50000, hi: 100000 },
  { l: '€100–250k', lo: 100000, hi: 250000 },
  { l: '€250–500k', lo: 250000, hi: 500000 },
  { l: '€500k–1M', lo: 500000, hi: 1000000 },
  { l: '€1–2,5M', lo: 1000000, hi: 2500000 },
  { l: '>€2,5M', lo: 2500000, hi: 5000000 },
];

export const DEBT_BANDS = ['None', '<€250k', '€250k–1M', '€1–5M', '>€5M'];
export const REV_BANDS = ['<€1M', '€1–5M', '€5–10M', '€10–25M', '€25–50M', '>€50M'];
// NB: these exact strings are matched in DealWizardCard's compToBool() — keep the two in lockstep.
export const COMPETITIVE = ['Only one bank', '2+ banks', 'Broker or RFP', 'Not sure'];

/**
 * One-line explainer per financing type, shown as a hover tooltip on the purpose
 * chips. Banker-authored; inform-not-advise (describes what the instrument is and
 * how it is typically repaid/secured, never "you should").
 */
export const PURPOSE_BLURB: Record<FinancingPurpose, string> = {
  acquisition: 'Borrowing to buy another company or its shares, usually repaid from the profits the business generates.',
  equipment: 'Financing for machinery, vehicles, or tools, typically repaid over the useful life of the asset it buys.',
  real_estate: 'A loan to buy or build premises, spread over a long term and secured against the property itself.',
  working_capital: 'A flexible credit line for everyday cash flow, drawn and repaid as needed and reviewed each year.',
  refinancing: 'Replacing one or more existing loans with new financing, often to change the rate, term, or monthly cost.',
  expansion: 'General investment funding for growth, such as new sites, hiring, or stock, repaid from future earnings.',
  other: 'Any financing need that does not fit the categories above, described in your own words later.',
};

export const EBITDA_TIP =
  'Earnings Before Interest, Taxes, Depreciation and Amortisation — roughly, the operating cash the business generates before financing and tax.';

/** Ordinal words for the welcome line ("Let’s set up your {ordinal} deal").
 *  Beyond the list, fall back to "next" — an 11th+ deal reads "your next deal". */
export const ORDINALS = [
  'first', 'second', 'third', 'fourth', 'fifth',
  'sixth', 'seventh', 'eighth', 'ninth', 'tenth',
];

/** Ordinal word for the n-th deal (1-indexed). "deal" is accented in orange by
 *  the component. */
export function ordinalWord(n: number): string {
  return ORDINALS[n - 1] ?? 'next';
}

/** Per-step left-pane heading (the big prompt). `amount_wc` is the working-capital variant. */
export const TITLES: Record<string, string> = {
  purpose: 'What are you looking to finance?',
  amount: 'How much to borrow, and over how long?',
  amount_wc: 'How large a credit line do you need?',
  health: 'How is the business doing financially right now?',
  asset: 'Total cost and own contribution',
  ebitda: 'In a normal year, what does the business make before you pay yourself, before tax, and before loan repayments?',
  optional: 'A few details, if you have them',
  create: 'Last thing: name your deal',
};

/**
 * Per-step left-pane explanatory text (Banker-reworded across the council passes;
 * inform-not-advise, no lender named). `purpose_idle` is the resting welcome subtext;
 * on the purpose step a hovered/selected chip swaps in its PURPOSE_BLURB instead.
 */
export const INFORM: Record<string, string> = {
  purpose_idle: 'You’re setting up a deal. It’s a few short questions, and this is the first.',
  amount: 'Enter the amount you are looking to finance, and the duration of the loan.',
  amount_wc: 'Enter the credit limit you want to open. It is the ceiling you draw on and repay as cash flow needs, rather than a lump sum taken all at once. Round numbers are fine.',
  health: 'Pick the option closest to your day-to-day cash comfort. Read it from the cash you feel month to month, not the profit on paper.',
  asset: 'Enter the full cost of what you’re financing (the property, the equipment, or the business you’re buying), then pick the share you can put in yourself.',
  ebitda: 'Pick the band that comes closest. A rough figure is fine. This is what a bank calls your EBITDA: the operating cash the business makes before financing and tax. It is the main figure a bank uses to size how much you can borrow.',
  optional: 'All three are optional. Answer what you know and skip the rest. Each answer sharpens the set of comparable deals your terms are read against, so the more you add, the closer the comparison.',
  create: 'The name is filled in from your answers, so keep it or type your own. Then set how soon you need the financing. Nothing here reaches a lender: the deal stays private to your workspace.',
};

/** Short human labels for the section indicator. */
export const SECTION_LABEL: Record<string, string> = {
  purpose: 'Financing',
  amount: 'Amount',
  health: 'Health',
  asset: 'Security',
  ebitda: 'Earnings',
  optional: 'Optional',
  create: 'Create',
};
